import { UnifiedBillingAccount } from 'mbs-ui-shared@models/storage-accounts/UnifiedBillingAccount';
import { PagedResponse } from './Paging';
import { StorageType } from './StorageType.enum';

export enum ImmutabilityMode {
  Disabled = 0,
  Compliance = 1,
  Governance = 2
}

export class StorageBucket {
  public BucketName: string;
  public DestinationName: string;
  public Id: string;
  public ImmutabilityMode: ImmutabilityMode;
  public Region: string;
  public RegionDisplayName: string;
}

export class StorageBucketCamelCase {
  public bucketName: string;
  public bucketId?: string;
  public destinationName: string;
  public id: string;
  public immutabilityMode: ImmutabilityMode;
  public region: string;
  public regionDisplayName: string;
  public storageUsage?: number;
}

export enum StorageAccountState {
  FullyAccessible = 'FullyAccessible',
  ReadOnly = 'ReadOnly',
  Disabled = 'Disabled',
  Deleted = 'Deleted'
}

export class StorageAccountLegacy {
  public AzureType: number;
  public Buckets?: StorageBucket[];
  public Id: string;
  public IsNonArchiveSyntheticSupported: boolean;
  public IsSyntheticSupported: boolean;
  public MinimumStorageDuration: number;
  public Name: string;
  public S3CompatibleSynthetic: boolean;
  public StorageAccountState?: StorageAccountState;
  public StorageType: StorageType;
  public disabled?: boolean; // internal property
}

export type StorageAccountV1 = {
  azureType: number;
  storageType: StorageType;
  buckets?: StorageBucketCamelCase[];
  id: string;
  isLocal?: boolean;
  isNonArchiveSyntheticSupported: boolean;
  isSyntheticSupported: boolean;
  // The backend ships these two fields PascalCase inside the otherwise camelCase payload (same as in SAWithDestinations)
  MinimumStorageDuration: number;
  name: string;
  S3CompatibleSynthetic: boolean;
  storageAccountState?: StorageAccountState;
  disabled?: boolean; // internal property
};

export type StorageAccountV2 = {
  id: string;
  displayName: string;
  azureType: number;
  cloudStorageId: string;
  destinationCount: number;
  storageType: StorageType;
  usersCount: number;
  storageUsage: number;
  isPrivateStorage: boolean;
  supportedBucketLocation: boolean;
  supportedStorageClass: boolean;
  supportedS3Accelerator: boolean;
  supportedExternalBucket: boolean;
  supportedLifecycleRules: boolean;
  supportedDeletion: boolean;
  supportedCapacityReport: boolean;
  supportedCloudBucketID: boolean;
  isRemovableDataStorage: boolean;
  isResellerAccount: boolean;
  isReadOnly: boolean;
  isDefaultObjectLockSupportedForNew: true;
  isDefaultObjectLockSupportedForExisting: true;
  isGfsObjectLockSupported: true;
  isDeleting: boolean;
  dateCreated: string;
  minimumStorageDuration: number;
  accountS3LifecycleRule: S3LifecycleRules;
  accountAzureLifecycleRule: AzureLifecycleRules;
  unifiedBillingAccount: UnifiedBillingAccount;
};

export type StorageAccountV2ListUnit = {
  id: string;
  displayName: string;
  cloudStorageId: string;
  destinationCount: number;
  usersCount: number;
  storageUsage: number;
  isAzureCredentialsEmpty: boolean;
  storageType: StorageType;
  isPrivateStorage: boolean;
  isRemovableDataStorage: boolean;
  isDeleting: boolean;
  dateCreated: string;
  minimumStorageDuration: string;
  countOfBucketsWithCustomPolicy: number;
  accountS3LifecycleRule: S3LifecycleRules;
  accountAzureLifecycleRule: AzureLifecycleRules;
  unifiedBillingAccount: UnifiedBillingAccount;
  appsUsersCount: number;
  isResellerAccount: boolean;
  isReadOnly: boolean;
  isDeletionCanBeCanceled: boolean;
  isAmazonGovStorage: boolean;
  isLifecycleRulesSupported: boolean;
  isUsedForSES: boolean;
  credential: {
    type: StorageType;
    displayName: string;
    amazonS3?: Record<string, any>;
    b2?: Record<string, any>;
    fileSystem?: Record<string, any>;
    ftp?: Record<string, any>;
    sftp?: Record<string, any>;
    googleCloud?: Record<string, any>;
    azure?: Record<string, any>;
    azureVm?: Record<string, any>;
    minio?: Record<string, any>;
    s3Compatible?: Record<string, any>;
    wasabi?: Record<string, any>;
  };
};

export type StorageAccountRegion = {
  displayName: string;
  systemName: string;
};

export type BucketInfo = {
  bucketName: string;
  cloudBucketID?: string;
  immutabilitySupported?: boolean;
  isGfsObjectLockEnabled?: boolean;
  objectLockPeriodDays?: number;
  region?: StorageAccountRegion;
  s3AcceleratorEnabled?: boolean;
  warnings?: string[];
  isGFSObjectLockSupported?: boolean;
};

export type SetDestinationType = {
  createNewBucket?: boolean;
  displayBucketName: string;
  isGfsObjectLockEnabled?: boolean;
  objectLockPeriodDays?: number;
  googleStorageClass?: number;
  assignToCompanies?: string[];
  bucket: BucketInfo;
  isExternalBucket?: boolean;
  credential?: { code: string; redirectUri: string };
};

export type SetStorageType = {
  destination?: SetDestinationType;
  arnRoleOptions?: {
    restrictBuckets: boolean;
    allowedBuckets: string[];
  };
};

export type S3LifecycleRules = {
  usingS3ToStandardIADays: number | null;
  usingS3ToOneZoneIADays: number | null;
  usingS3ToGlacierDays: number | null;
  usingS3ToGlacierInstantRetrievalDays: number | null;
  usingS3ToGlacierDeepArchiveDays: number | null;
  usingS3ToIntelligentTierDays: number | null;
  usingS3CleanupUploadsDays: number | null;
};

export type AzureLifecycleRules = {
  toCool: number;
  toArchive: number;
};

export type LifecycleRulesRequest<Data, Credential> = {
  lifecyclePolicy: Data;
  credential?: Credential;
  overwriteAll?: boolean;
};

export type S3LifecycleRulesRequest = LifecycleRulesRequest<S3LifecycleRules, undefined>;
export type AzureLifecycleRulesRequest = LifecycleRulesRequest<
  AzureLifecycleRules,
  {
    code: string;
    redirectUri: string;
  }
>;

export type Destination = {
  id: string;
  accountId: string;
  bucketId?: string;
  displayStorageName: string;
  bucket: BucketInfo;
  displayName: string;
  storageType: StorageType;
  users: number;
  appsUsers: number;
  mailUsing: boolean;
  isGfsObjectLockEnabled: boolean;
  objectLockPeriodDays: 0;
  s3LifecycleRule: S3LifecycleRules & { isAccountPolicy: boolean };
  azureLifecycleRule: AzureLifecycleRules & { isAccountPolicy: boolean };
  isRemovable: boolean;
  assignedCompanies: string[];
  isExternalBucket: boolean;
  isReadOnly: boolean;
  sizePercent?: number;
  totalSize?: number;
};

export type GoogleStorageClass = {
  id: GoogleStorageClassEnum;
  name: string;
};

export type PartialSuccessResponse = { extensions: Record<string, string>; instance: string };

export type StorageDestinationListUnit = {
  id: string;
  accountId: string;
  storageAccountName: string;
  bucketName: string;
  destinationName: string;
  storageType: StorageType;
  storageAccountState: StorageAccountState;
  isGfsObjectLockEnabled: boolean;
  objectLockPeriodDays: number;
  region: StorageAccountRegion;
};

export type StorageDestinationWithLimit = StorageDestinationListUnit & {
  storageLimitId?: number;
  storageLimitName?: string;
};

export type DestinationsResponse = PagedResponse<Destination>;

export enum AssignedType {
  company = 0,
  user = 1
}

export type AssignedTableItem = { id: string; name: string; type: AssignedType };

export type DestinationAssignedResponse = PagedResponse<AssignedTableItem>;

export enum StorageTypeState {
  Enabled = 0,
  OnlyExisting = 1,
  OnlyNew = 2
}

export type StorageWizardCompanies = {
  companyIds: string[];
  destinationCompanyIds: string[];
  canRestrictByCompanies: boolean;
};

export type StoragesAvailableForCreation = { type: StorageType; count: number; state: StorageTypeState }[];

export type ValidateResponse = {
  ignoreCertificate: boolean;
  useSignatureVersion4: boolean;
} | null;

export interface ValidateStorageRequest {
  type: StorageType;
  displayName: string;
  amazonS3?: {
    arnRole: string;
    accessKey: string;
    secretKey: string;
    restrictBuckets: boolean;
    isGov: boolean;
    storageType: StorageType;
    authType: S3AuthType;
  };
  b2?: {
    accountId: string;
    applicationKey: string;
  };
  fileSystem?: {
    path: string;
  };
  ftp?: {
    user: string;
    password: string;
    address: string;
    doNotCheckCredentials: boolean;
    useSSL: boolean;
    keepAlive: boolean;
    usePassiveMode: boolean;
  };
  sftp?: {
    user: string;
    password: string;
    address: string;
    doNotCheckCredentials: boolean;
    keepAlive: boolean;
  };
  googleCloud?: {
    serviceAccountEmail: string;
    projectId: string;
    binaryKeyInBase64: string;
  };
  azure?: {
    accountType: string;
    endpoint: string;
    accountName: string;
    sharedKey: string;
  };
  minio?: {
    accessKey: string;
    secretKey: string;
    httpEndpoint: string;
    httpsEndpoint: string;
    ignoreCertificate: boolean;
    privateStorage: boolean;
    useVHostRequestStyle: boolean;
    isSyntheticBackupAllowed: boolean;
    isVersioningEnabled: boolean;
    isPrivateStorage: boolean;
    useNativeMultipartUpload: boolean;
    keepCredentialsOnEndpoint: boolean;
  };
  s3Compatible?: {
    accessKey: string;
    secretKey: string;
    httpEndpoint: string;
    httpsEndpoint: string;
    ignoreCertificate: boolean;
    privateStorage: boolean;
    signatureVersion: number;
    useNativeMultipartUpload: boolean;
    syntheticBackup: boolean;
    useVHostRequestStyle: boolean;
    isSyntheticBackupAllowed: boolean;
    isVersioningEnabled: boolean;
    isPrivateStorage: boolean;
    useSignatureVersion4: boolean;
    keepCredentialsOnEndpoint: boolean;
  };
  wasabi?: {
    arnRole: string;
    accessKey: string;
    secretKey: string;
  };
  azureVm?: {
    subscriptionId: string;
    refreshToken: string;
    tenantId: string;
    subscriptionDisplayName: string;
  };
  iDrive?: {
    accessKey: string;
    secretKey: string;
  };
}

export enum GoogleStorageClassEnum {
  MultiRegional = 5
}

export type AzureVMSubscription = {
  tenantId: string;
  subscriptionId: string;
  subscriptionDisplayName: string;
};

export type AzureVMSubscriptionsResponse = {
  refreshToken: string;
  subscriptions: AzureVMSubscription[];
};

export enum S3AuthType {
  Manual,
  MBS,
  Legacy
}

export type S3CompatibleCredential = {
  useVHostRequestStyle: boolean;
  isSyntheticBackupAllowed: boolean;
  isVersioningEnabled: boolean;
  useSignatureVersion4: boolean;
  accessKey: string;
  secretKey: string;
  useNativeMultipartUpload: boolean;
  httpEndpoint: string;
  httpsEndpoint: string;
  ignoreCertificate: boolean;
  keepCredentialsOnEndpoint: boolean;
  isPrivateStorage: boolean;
};

export type S3CompatibilityCheckResponse = {
  taskId: string;
  status: number;
  s3Compatible?: S3CompatibleCredential;
};

type StorageAccountsPostSorting = {
  orderBy?: string;
  orderByProperty?: string;
  isDescOrderBy?: boolean;
};

type StorageAccountsPostPaging = {
  offset: number;
  limit: number;
};

export type GetStorageAccountDestinationsPostBody = {
  filter: {
    hasAbandonedData?: boolean;
    immutabilityEnabled?: boolean;
    companyIds?: string[];
    search?: string;
  };
  sorting: StorageAccountsPostSorting;
  paging: StorageAccountsPostPaging;
};

export type GetStorageAccountsPostBody = {
  filter: {
    hasAbandonedData?: boolean;
    companyIds?: string[];
    search?: string;
  };
  sorting: StorageAccountsPostSorting;
  paging: StorageAccountsPostPaging;
};
